import { Project, ItemStatus } from '../models/types';
import { createProjectFromTemplate, DEFAULT_FRAMEWORK_TEMPLATE } from '../models/defaultTemplate';

/**
 * Service for storing and retrieving projects
 * Currently uses localStorage but designed to be easily replaced with a proper backend
 */
export class StorageService {
  private readonly PROJECTS_KEY = 'requirements_tracker_projects';
  private readonly CURRENT_PROJECT_KEY = 'requirements_tracker_current_project';

  /**
   * Save a project to storage
   * @param project The project to save
   * @returns The saved project
   */
  public saveProject(project: Project): Project {
    // Update the timestamp
    project.updatedAt = new Date();
    
    // Get existing projects
    const projects = this.getAllProjects();
    
    // Find and replace if exists, otherwise add
    const index = projects.findIndex(p => p.id === project.id);
    if (index >= 0) {
      projects[index] = project;
    } else {
      projects.push(project);
    }
    
    // Save back to storage
    localStorage.setItem(this.PROJECTS_KEY, JSON.stringify(projects));
    
    // Update current project if this is the current one
    const currentProject = this.getCurrentProject();
    if (currentProject && currentProject.id === project.id) {
      this.setCurrentProject(project);
    }
    
    return project;
  }

  /**
   * Get a project by ID
   * @param id The project ID to retrieve
   * @returns The project if found, null otherwise
   */
  public getProject(id: string): Project | null {
    const projects = this.getAllProjects();
    const project = projects.find(p => p.id === id) || null;
    return project;
  }

  /**
   * Get all projects
   * @returns Array of all projects
   */
  public getAllProjects(): Project[] {
    const projectsJson = localStorage.getItem(this.PROJECTS_KEY);
    if (!projectsJson) return [];
    
    try {
      return JSON.parse(projectsJson);
    } catch (e) {
      console.error('Failed to parse projects from storage', e);
      return [];
    }
  }

  /**
   * Delete a project
   * @param id The ID of the project to delete
   * @returns true if deleted, false if not found
   */
  public deleteProject(id: string): boolean {
    const projects = this.getAllProjects();
    const initialLength = projects.length;
    
    const filteredProjects = projects.filter(p => p.id !== id);
    localStorage.setItem(this.PROJECTS_KEY, JSON.stringify(filteredProjects));
    
    // If this was the current project, clear it
    const currentProject = this.getCurrentProject();
    if (currentProject && currentProject.id === id) {
      this.clearCurrentProject();
    }
    
    return filteredProjects.length < initialLength;
  }

  /**
   * Set the current active project
   * @param project The project to set as current
   */
  public setCurrentProject(project: Project): void {
    localStorage.setItem(this.CURRENT_PROJECT_KEY, JSON.stringify(project));
  }

  /**
   * Get the current active project
   * @returns The current project or null if none is set
   */
  public getCurrentProject(): Project | null {
    const projectJson = localStorage.getItem(this.CURRENT_PROJECT_KEY);
    if (!projectJson) return null;
    
    try {
      return JSON.parse(projectJson);
    } catch (e) {
      console.error('Failed to parse current project from storage', e);
      return null;
    }
  }

  /**
   * Clear the current project
   */
  public clearCurrentProject(): void {
    localStorage.removeItem(this.CURRENT_PROJECT_KEY);
  }

  /**
   * Create a new project
   * @param name Project name
   * @param description Project description
   * @returns The newly created project
   */
  public createNewProject(name: string, description: string): Project {
    const project = createProjectFromTemplate(name, description);
    this.saveProject(project);
    this.setCurrentProject(project);
    return project;
  }

  /**
   * Initialize demo data if storage is empty
   */
  public initializeIfEmpty(): void {
    const projects = this.getAllProjects();
    
    if (projects.length === 0) {
      // Create a demo project
      const demoProject = createProjectFromTemplate(
        "Sample Project", 
        "A demonstration project showing how to use the platform development framework."
      );
      
      // Add some progress to make it look more realistic
      demoProject.sections[0].subsections[0].items[0].status = ItemStatus.Completed;
      demoProject.sections[0].subsections[0].items[1].status = ItemStatus.InProgress;
      
      this.saveProject(demoProject);
      this.setCurrentProject(demoProject);
    }
  }
}

export const storageService = new StorageService();
